import { useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate, todayISO } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowRight, Trash, ArrowsLeftRight, Vault } from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

export default function MovimientosCajasPage() {
  const [cajas, setCajas] = useState([]);
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    caja_origen_id: "",
    caja_destino_id: "",
    monto: "",
    fecha: todayISO(),
    concepto: "",
  });

  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        api.get("/cajas"),
        api.get("/cajas/transferencias"),
      ]);
      setCajas(cRes.data || []);
      setTransferencias(tRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cajasCompatibles = useMemo(() => {
    if (!form.caja_origen_id) return cajas;
    const origen = cajas.find((c) => c.id === form.caja_origen_id);
    if (!origen) return cajas;
    // Sólo cajas de la misma moneda y distintas de la origen
    return cajas.filter((c) => c.id !== origen.id && (c.moneda || "ARS") === (origen.moneda || "ARS"));
  }, [cajas, form.caja_origen_id]);

  const cajaOrigen = useMemo(() => cajas.find((c) => c.id === form.caja_origen_id) || null, [cajas, form.caja_origen_id]);
  const cajaDestino = useMemo(() => cajas.find((c) => c.id === form.caja_destino_id) || null, [cajas, form.caja_destino_id]);

  const openNuevo = () => {
    setForm({ caja_origen_id: "", caja_destino_id: "", monto: "", fecha: todayISO(), concepto: "" });
    setOpen(true);
  };

  const guardar = async () => {
    if (!form.caja_origen_id) return toast.error("Elegí la caja origen");
    if (!form.caja_destino_id) return toast.error("Elegí la caja destino");
    if (form.caja_origen_id === form.caja_destino_id) return toast.error("Origen y destino deben ser diferentes");
    const monto = Number(form.monto);
    if (!monto || monto <= 0) return toast.error("El monto debe ser mayor a 0");
    if (!form.fecha) return toast.error("Elegí una fecha");

    setSaving(true);
    try {
      await api.post("/cajas/transferencia", {
        caja_origen_id: form.caja_origen_id,
        caja_destino_id: form.caja_destino_id,
        monto,
        fecha: form.fecha,
        concepto: form.concepto || "Traspaso entre cajas",
      });
      toast.success("Transferencia registrada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la transferencia");
    } finally {
      setSaving(false);
    }
  };

  const revertir = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      await api.delete(`/cajas/transferencia/${revertTarget.id}`);
      toast.success("Transferencia revertida");
      setRevertTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <Topbar />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowsLeftRight size={26} weight="duotone" className="text-amber-700" />
            Movimientos entre Cajas
          </h1>
          <p className="text-sm text-slate-500">
            Traspasos de saldos entre cajas (alivios de caja, depósitos internos, etc.)
          </p>
        </div>
        <Button onClick={openNuevo} className="gap-2" data-testid="mov-cajas-nuevo-btn">
          <Plus size={16} /> Nuevo traspaso
        </Button>
      </div>

      {/* Saldos actuales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {cajas.map((c) => (
          <div
            key={c.id}
            className="rounded-lg border bg-white p-3 flex flex-col gap-1"
            data-testid={`mov-cajas-saldo-${c.id}`}
          >
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Vault size={13} /> {c.nombre}
            </div>
            <div className="text-lg font-mono font-semibold text-slate-800">
              {fmtMoney(c.saldo || 0)}{" "}
              <span className="text-[10px] text-slate-400">{c.moneda || "ARS"}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Listado de transferencias */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Origen</th>
              <th className="text-center px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Destino</th>
              <th className="text-right px-3 py-2">Monto</th>
              <th className="text-left px-3 py-2">Concepto</th>
              <th className="text-right px-3 py-2 w-16">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-6">Cargando…</td></tr>
            ) : transferencias.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-slate-400 py-8">
                  Todavía no hay transferencias entre cajas.
                </td>
              </tr>
            ) : (
              transferencias.map((t) => (
                <tr key={t.id} className="border-t hover:bg-slate-50" data-testid={`mov-cajas-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono text-slate-700">{fmtDate(t.fecha)}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 font-normal">
                      {t.caja_origen_nombre}
                    </Badge>
                  </td>
                  <td className="text-center text-slate-400"><ArrowRight size={14} /></td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal">
                      {t.caja_destino_nombre}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {fmtMoney(t.monto)}{" "}
                    <span className="text-[10px] text-slate-400">{t.moneda || "ARS"}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{t.concepto || "-"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRevertTarget(t)}
                      className="h-7 px-2 text-red-500 hover:bg-red-50"
                      title="Revertir transferencia"
                      data-testid={`mov-cajas-revert-${t.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog Nuevo Traspaso */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="mov-cajas-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo traspaso entre cajas</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Caja origen</Label>
              <Select value={form.caja_origen_id} onValueChange={(v) => setForm((f) => ({ ...f, caja_origen_id: v, caja_destino_id: "" }))}>
                <SelectTrigger data-testid="mov-cajas-origen">
                  <SelectValue placeholder="Seleccionar caja de origen…" />
                </SelectTrigger>
                <SelectContent>
                  {cajas.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre} — {fmtMoney(c.saldo || 0)} {c.moneda || "ARS"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Caja destino</Label>
              <Select value={form.caja_destino_id} onValueChange={(v) => setForm((f) => ({ ...f, caja_destino_id: v }))} disabled={!form.caja_origen_id}>
                <SelectTrigger data-testid="mov-cajas-destino">
                  <SelectValue placeholder={form.caja_origen_id ? "Seleccionar caja de destino…" : "Elegí primero la caja origen"} />
                </SelectTrigger>
                <SelectContent>
                  {cajasCompatibles.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre} — {fmtMoney(c.saldo || 0)} {c.moneda || "ARS"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.caja_origen_id && cajasCompatibles.length === 0 && (
                <p className="text-xs text-amber-700">No hay otras cajas con la misma moneda ({cajaOrigen?.moneda || "ARS"}).</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <DateInput
                  value={form.fecha}
                  onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
                  data-testid="mov-cajas-fecha"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Monto</Label>
                <NumberInput
                  step="0.01"
                  min="0"
                  value={form.monto}
                  onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
                  placeholder="0.00"
                  data-testid="mov-cajas-monto"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Concepto <span className="text-slate-400 text-xs">(opcional)</span></Label>
              <Input
                value={form.concepto}
                onChange={(e) => setForm((f) => ({ ...f, concepto: e.target.value }))}
                placeholder="Ej: Alivio de caja / Depósito diario"
                data-testid="mov-cajas-concepto"
              />
            </div>

            {cajaOrigen && cajaDestino && Number(form.monto) > 0 && (
              <div className="rounded bg-slate-50 border p-2 text-xs text-slate-600 flex items-center gap-2">
                <span className="font-medium text-rose-700">{cajaOrigen.nombre}</span>
                <ArrowRight size={12} />
                <span className="font-medium text-emerald-700">{cajaDestino.nombre}</span>
                <span className="ml-auto font-mono">{fmtMoney(Number(form.monto))} {cajaOrigen.moneda || "ARS"}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} data-testid="mov-cajas-guardar">
              {saving ? "Guardando…" : "Guardar traspaso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revert */}
      <AlertDialog open={!!revertTarget} onOpenChange={(o) => { if (!o) setRevertTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir esta transferencia?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán los dos movimientos y se restablecerán los saldos de{" "}
              <b>{revertTarget?.caja_origen_nombre}</b> y <b>{revertTarget?.caja_destino_nombre}</b>{" "}
              por {revertTarget ? fmtMoney(revertTarget.monto) : ""} {revertTarget?.moneda}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={revertir} disabled={reverting} className="bg-red-600 hover:bg-red-700">
              {reverting ? "Revirtiendo…" : "Revertir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
